import { Badge, Box, Checkbox, HStack, Text, VStack } from '@chakra-ui/react';

// 教職員通知設定的矩陣：列＝一種通知，欄＝四種職位。
//
// 【為什麼是一張表而不是一堆獨立開關】管理員要回答的問題是「櫃檯到底收得到哪些東西」，
// 那是一個橫向的問題。做成一項一項的開關就必須把六個區塊全部讀完才能回答，
// 而且看不出「某個職位什麼都收不到」這種明顯不對的狀態。
//
// 【為什麼每一列都有一句白話說明】沒有這句，這張表沒有人看得懂，設定畫面就變成
// 沒有人敢動的裝飾——那等於這個功能沒做。說明文字由後端提供（見
// app/core/staff_permissions.py 的 CAPABILITY_META），不在這裡寫死。
//
// 【為什麼沒有把管理員／超管那兩欄鎖住】盤點文件對「功能類」能力建議把管理層鎖住，
// 避免有人把自己鎖在門外（例如取消了「進入機構設定」就再也改不回來）。
// **通知類沒有這個風險**：少收一則通知不會讓任何人失去進入設定畫面的能力，
// 而且「接送完成通知刻意不給管理員」這條現有規則本身就需要管理員那一格可以是空的。
// 把它鎖住反而會讓這張表無法表達系統現在真正的行為。

export default function StaffNotificationMatrix({
    roles = [],
    capabilities = [],
    value = {},
    onToggle,
    isDisabled = false,
}) {
    // 依 group 分節顯示。分節純粹是為了讓六列不要看起來像一片欄柵，
    // 沒有任何行為意義（後端不認識 group）。
    const groups = [];
    capabilities.forEach((cap) => {
        const last = groups[groups.length - 1];
        if (last && last.name === cap.group) last.items.push(cap);
        else groups.push({ name: cap.group, items: [cap] });
    });

    const isDefault = (cap) => {
        const current = [...(value[cap.name] || [])].sort();
        const base = [...(cap.default_roles || [])].sort();
        return current.length === base.length && current.every((r, i) => r === base[i]);
    };

    return (
        <VStack align="stretch" spacing={0}>
            {/* 欄位標題。sticky 沒有意義（只有六列），所以就是一列普通的標題。 */}
            <HStack
                spacing={0}
                py={2}
                borderBottom="2px solid"
                borderColor="neutral.200"
                display={{ base: 'none', md: 'flex' }}
            >
                <Box flex="1" />
                {roles.map((role) => (
                    <Box key={role.name} w="82px" textAlign="center">
                        <Text fontSize="xs" fontWeight="bold" color="neutral.600">
                            {role.label}
                        </Text>
                    </Box>
                ))}
            </HStack>

            {groups.map((group) => (
                <Box key={group.name}>
                    <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="var(--fg-muted)"
                        mt={4}
                        mb={1}
                        letterSpacing="0.05em"
                    >
                        {group.name}
                    </Text>

                    {group.items.map((cap) => (
                        <Box
                            key={cap.name}
                            py={3}
                            borderTop="1px solid"
                            borderColor="neutral.200"
                            opacity={isDisabled ? 0.55 : 1}
                        >
                            <HStack
                                spacing={0}
                                align={{ base: 'stretch', md: 'center' }}
                                flexDirection={{ base: 'column', md: 'row' }}
                            >
                                <Box flex="1" pr={{ md: 3 }}>
                                    <HStack spacing={2}>
                                        <Text fontSize="sm" fontWeight="medium" color="neutral.800">
                                            {cap.label}
                                        </Text>
                                        {/* 「已調整」讓管理員一眼看出哪幾列被動過。
                                            沒有這個標記的話，一張全是勾勾的表看不出
                                            「這是預設值」還是「有人設定成剛好一樣」。 */}
                                        {!isDefault(cap) && (
                                            <Badge colorScheme="orange" fontSize="10px">已調整</Badge>
                                        )}
                                    </HStack>
                                    <Text fontSize="xs" color="var(--fg-muted)" mt={0.5}>
                                        {cap.description}
                                    </Text>
                                </Box>

                                <HStack spacing={0} mt={{ base: 2, md: 0 }}>
                                    {roles.map((role) => (
                                        <Box
                                            key={role.name}
                                            w={{ base: 'auto', md: '82px' }}
                                            mr={{ base: 4, md: 0 }}
                                            textAlign="center"
                                        >
                                            <Checkbox
                                                colorScheme="brand"
                                                isChecked={(value[cap.name] || []).includes(role.name)}
                                                isDisabled={isDisabled}
                                                onChange={(e) => onToggle(cap.name, role.name, e.target.checked)}
                                                aria-label={`${cap.label} - ${role.label}`}
                                            >
                                                {/* 窄螢幕沒有表頭欄位，每個勾選框自己帶職位名稱，
                                                    否則手機上會變成四個沒有標籤的方框。 */}
                                                <Text
                                                    fontSize="xs"
                                                    color="neutral.600"
                                                    display={{ base: 'inline', md: 'none' }}
                                                >
                                                    {role.label}
                                                </Text>
                                            </Checkbox>
                                        </Box>
                                    ))}
                                </HStack>
                            </HStack>
                        </Box>
                    ))}
                </Box>
            ))}
        </VStack>
    );
}
